import { inject, Injectable } from '@angular/core';
import {
  CheckoutValidator,
  TimeSlot,
  TimeSlotAdditionalParams,
  TimeSlotOption,
  TimeSlotsApiParams,
  ValidationErrorKeys,
  ValidationErrors,
} from '@checkout-v2/const';
import { TimeSlotsFacade } from '@checkout-v2/data-access';
import { StateUtils } from '@spartacus/core';
import { combineLatest, map, Observable } from 'rxjs';
import { ServicesService } from './services.service';

@Injectable({
  providedIn: 'root',
})
export class TimeSlotsService extends CheckoutValidator {
  private readonly timeSlotsFacade = inject(TimeSlotsFacade);
  private readonly servicesService = inject(ServicesService);

  getTimeSlotsState(): Observable<StateUtils.LoaderState<TimeSlot[]>> {
    return this.timeSlotsFacade.getTimeSlotsState();
  }

  getTimeSlotsLoading(): Observable<boolean> {
    return this.timeSlotsFacade.getTimeSlotsLoading();
  }

  getTimeSlotsError(): Observable<boolean> {
    return this.timeSlotsFacade.getTimeSlotsError();
  }

  getTimeSlotsSuccess(): Observable<boolean> {
    return this.timeSlotsFacade.getTimeSlotsSuccess();
  }

  getTimeSlots(): Observable<TimeSlot[]> {
    return this.timeSlotsFacade.getTimeSlots();
  }

  getSelectedTimeSlotId(): Observable<string | undefined> {
    return this.timeSlotsFacade.getSelectedTimeSlotId();
  }

  getSelectedTimeSlot(): Observable<TimeSlotOption | undefined> {
    return this.timeSlotsFacade.getSelectedTimeSlot();
  }

  selectTimeSlot(id: string, additionalParams?: TimeSlotAdditionalParams): void {
    this.timeSlotsFacade.selectTimeSlot(id, additionalParams);
  }

  resetTimeSlot(): void {
    this.timeSlotsFacade.resetTimeSlot();
  }

  loadTimeSlotsForCustomDate(params: TimeSlotsApiParams): void {
    this.timeSlotsFacade.loadTimeSlotsForCustomDate(params);
  }

  selectCustomDate(date: string): void {
    return this.timeSlotsFacade.selectCustomDate(date);
  }

  getSelectedCustomDate(): Observable<string | undefined> {
    return this.timeSlotsFacade.getSelectedCustomDate();
  }

  getTimeSlotsForCustomDateState(): Observable<StateUtils.LoaderState<TimeSlot[]>> {
    return this.timeSlotsFacade.getTimeSlotsForCustomDateState();
  }

  getTimeSlotsForCustomDateLoading(): Observable<boolean> {
    return this.timeSlotsFacade.getTimeSlotsForCustomDateLoading();
  }

  getTimeSlotsForCustomDateError(): Observable<boolean> {
    return this.timeSlotsFacade.getTimeSlotsForCustomDateError();
  }

  getTimeSlotsForCustomDateSuccess(): Observable<boolean> {
    return this.timeSlotsFacade.getTimeSlotsForCustomDateSuccess();
  }

  isEmptyOrHasError(): Observable<boolean> {
    return combineLatest({
      servicesIsEmptyOrHasError: this.servicesService.isEmptyOrHasError(),
      timeSlotsState: this.getTimeSlotsState(),
      timeSlotsForCustomDateState: this.getTimeSlotsForCustomDateState(),
      timeSlots: this.getTimeSlots(),
    }).pipe(
      map(
        ({ servicesIsEmptyOrHasError, timeSlotsState, timeSlotsForCustomDateState, timeSlots }) =>
          servicesIsEmptyOrHasError ||
          !!timeSlotsState.error ||
          timeSlotsForCustomDateState.error ||
          (!!timeSlots && timeSlots.length === 0),
      ),
    );
  }

  getValidationErrors(): Observable<ValidationErrors> {
    return combineLatest({
      isEmptyOrHasError: this.isEmptyOrHasError(),
      timeSlots: this.getTimeSlots(),
      activeTimeSlot: this.getSelectedTimeSlot(),
    }).pipe(
      map(({ isEmptyOrHasError, activeTimeSlot }) => {
        const validationErrors: ValidationErrors = {};

        if (!isEmptyOrHasError && !activeTimeSlot) {
          validationErrors[ValidationErrorKeys.TIME_SLOT_REQUIRED] = 'выберите время доставки';
        }

        return validationErrors;
      }),
    );
  }
}
